/**
 * Calendrier des tirages du panneau de recherche, commun à nav.js (pages serveur) et à
 * l'application React : chaque jour qui a un tirage mène à /tirage/{date}/. Les dates
 * viennent de /_api/public/tirage-dates, chargées à la première ouverture seulement.
 * Même balisage que le calendrier « Choisir un tirage » (classes mag-picker__cal-*).
 */
const MOIS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const pad = (n: number) => String(n).padStart(2, "0");

export interface CalendrierDeps {
  fetchDates: () => Promise<string[]>;
  /** Date du tirage de la page en cours (AAAA-MM-JJ), mise en avant. */
  courante?: string | null;
}

export async function chargerDatesTirages(fetchFn: typeof fetch = fetch): Promise<string[]> {
  const r = await fetchFn("/_api/public/tirage-dates");
  const d = (await r.json()) as { dates?: unknown };
  return Array.isArray(d.dates) ? d.dates.filter((x): x is string => typeof x === "string") : [];
}

export function dateDeLaPage(pathname: string): string | null {
  return pathname.match(/^\/(?:analyses\/)?tirage\/(\d{4}-\d{2}-\d{2})\//)?.[1] ?? null;
}

export function monterCalendrier(root: HTMLElement, deps: CalendrierDeps): { ouvrir: () => Promise<void> } {
  const doc = root.ownerDocument;
  root.classList.add("mag-picker__cal");
  root.innerHTML = '<div class="mag-picker__cal-nav">'
    + '<button type="button" class="mag-picker__cal-prev" aria-label="Mois précédent"><span aria-hidden="true">‹</span></button>'
    + '<span class="mag-picker__cal-label" aria-live="polite"></span>'
    + '<button type="button" class="mag-picker__cal-next" aria-label="Mois suivant"><span aria-hidden="true">›</span></button></div>'
    + '<div class="mag-picker__cal-weekdays" aria-hidden="true"><span>L</span><span>M</span><span>M</span><span>J</span><span>V</span><span>S</span><span>D</span></div>'
    + '<div class="mag-picker__cal-grid"></div>';
  const label = root.querySelector<HTMLElement>(".mag-picker__cal-label")!;
  const grille = root.querySelector<HTMLElement>(".mag-picker__cal-grid")!;
  const prev = root.querySelector<HTMLButtonElement>(".mag-picker__cal-prev")!;
  const next = root.querySelector<HTMLButtonElement>(".mag-picker__cal-next")!;
  let dates: Set<string> | null = null;
  let min = "";
  let max = "";
  let y = 0;
  let m = 0;

  const rendre = () => {
    label.textContent = `${MOIS[m - 1]} ${y}`;
    const cle = `${y}-${pad(m)}`;
    prev.disabled = min !== "" && cle <= min.slice(0, 7);
    next.disabled = max !== "" && cle >= max.slice(0, 7);
    grille.replaceChildren();
    const decalage = (new Date(y, m - 1, 1).getDay() + 6) % 7;
    for (let i = 0; i < decalage; i++) {
      const vide = doc.createElement("span");
      vide.className = "mag-picker__cal-day mag-picker__cal-day--empty";
      vide.setAttribute("aria-hidden", "true");
      grille.appendChild(vide);
    }
    const jours = new Date(y, m, 0).getDate();
    for (let d = 1; d <= jours; d++) {
      const iso = `${cle}-${pad(d)}`;
      const dispo = dates?.has(iso) ?? false;
      const jour = doc.createElement(dispo ? "a" : "span");
      jour.textContent = String(d);
      if (dispo) {
        (jour as HTMLAnchorElement).href = `/tirage/${iso}/`;
        jour.setAttribute("aria-label", `Voir le tirage du ${d} ${MOIS[m - 1]} ${y}`);
        jour.className = `mag-picker__cal-day mag-picker__cal-day--${iso === deps.courante ? "active" : "avail"}`;
        if (iso === deps.courante) jour.setAttribute("aria-current", "page");
      } else {
        jour.className = "mag-picker__cal-day mag-picker__cal-day--unavail";
        jour.setAttribute("aria-disabled", "true");
      }
      grille.appendChild(jour);
    }
  };
  const decaler = (pas: number) => {
    m += pas;
    if (m < 1) { m = 12; y--; }
    if (m > 12) { m = 1; y++; }
    rendre();
  };
  prev.addEventListener("click", () => decaler(-1));
  next.addEventListener("click", () => decaler(1));

  return {
    ouvrir: async () => {
      if (dates === null) {
        try {
          const liste = [...(await deps.fetchDates())].sort();
          dates = new Set(liste);
          min = liste[0] ?? "";
          max = liste[liste.length - 1] ?? "";
        } catch {
          dates = new Set();
        }
        const base = deps.courante ?? (max || new Date().toISOString().slice(0, 10));
        y = Number(base.slice(0, 4));
        m = Number(base.slice(5, 7));
      }
      rendre();
    },
  };
}
